import styled from 'styled-components';
import { useMemo, ReactNode, useState, useRef } from 'react';
import { s } from '../components/PeriodStatsPage.styles';
import { formatCurrency } from '../../../func/Function';
import * as XLSX from 'xlsx';

export type Column<T> = {
  key: keyof T | string;
  label: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  render?: (item: T) => ReactNode;
  unit?: string;
};

type OptionButton = { key: string; label: string };

type TableProps<T> = {
  title: string;
  description?: string;
  selectionOptions?: ReactNode[];
  columns: Column<T>[];
  data: T[];
  avgKeys?: (keyof T)[];
  extraBottom?: ReactNode;
  initialVisibleCount?: number;
  onDataChange?: (key: string) => void;
  extraTop?: OptionButton[];
  loading?: boolean;
  isPremium?: boolean;
};

function MyTable<T extends Record<string, any>>({
  title,
  description,
  selectionOptions,
  columns,
  data,
  avgKeys = [],
  extraTop,
  extraBottom,
  initialVisibleCount,
  onDataChange,
  loading,
  isPremium = false,
}: TableProps<T>) {
  const [showAll, setShowAll] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(
    extraTop && extraTop.length > 0 ? extraTop[0].key : null,
  );
  const tableRef = useRef<HTMLTableElement>(null);

  const handleOptionClick = (optionKey: string) => {
    setSelectedOption(optionKey);
    if (onDataChange) onDataChange(optionKey);
  };

  const dragScroll = useDragScroll();

  const visibleCount = initialVisibleCount ?? data.length;

  const visible = useMemo(
    () => data.slice(0, showAll ? data.length : visibleCount),
    [showAll, data, visibleCount],
  );

  const avg = useMemo(() => {
    if (avgKeys.length === 0) return null;
    const sum: Record<string, number> = {};
    const count: Record<string, number> = {};
    avgKeys.forEach((key) => {
      sum[key as string] = 0;
      count[key as string] = 0;
    });

    visible.forEach((row) => {
      avgKeys.forEach((key) => {
        const val = Number(row[key]);
        if (!isNaN(val) && val !== 0) {
          sum[key as string] += val;
          count[key as string] += 1;
        }
      });
    });

    const result: Record<string, number> = {};
    avgKeys.forEach((key) => {
      const avgValue =
        count[key as string] > 0
          ? sum[key as string] / count[key as string]
          : 0;

      result[key as string] = Math.floor(avgValue * 100) / 100;
    });

    return result;
  }, [visible, avgKeys]);

  const handleDownloadExcel = () => {
    if (!tableRef.current) return;
    const wb = XLSX.utils.table_to_book(tableRef.current, {
      sheet: 'Sheet1',
    });
    XLSX.writeFile(wb, `${title}.xlsx`);
  };

  return (
    <Container>
      <Top>
        {title && <h3>{title}</h3>}
        <s.Button
          onClick={handleDownloadExcel}
          disabled={loading || !isPremium}
          aria-label="엑셀 저장"
        >
          <img
            style={{ width: '18px' }}
            src="assets/images/icon/download-black.svg"
            alt="엑셀 저장"
          />
        </s.Button>
      </Top>
      {description && (
        <Description dangerouslySetInnerHTML={{ __html: description }} />
      )}
      {loading ? (
        <s.LoaderWrap>
          <s.LoaderImg src="/assets/images/icon/loader.gif" alt="로딩중" />
        </s.LoaderWrap>
      ) : (
        <>
          {selectionOptions && selectionOptions.length > 0 && (
            <s.SelectedOptionBox>
              {selectionOptions.map((opt, idx) => (
                <s.SelectedOption key={idx}>{opt}</s.SelectedOption>
              ))}
            </s.SelectedOptionBox>
          )}

          {extraTop && extraTop.length > 0 && (
            <div style={{ width: '100%', marginTop: '16px', display: 'block' }}>
              <s.TabRow style={{ width: '100%' }}>
                {extraTop.map((btn) => (
                  <s.TabPill
                    style={{ flex: '100%' }}
                    key={btn.key}
                    $active={btn.key === selectedOption}
                    onClick={() => handleOptionClick(btn.key)}
                  >
                    {btn.label}
                  </s.TabPill>
                ))}
              </s.TabRow>
            </div>
          )}

          <TableWrap
            ref={dragScroll.ref}
            onMouseDown={dragScroll.onMouseDown}
            onMouseLeave={dragScroll.onMouseLeave}
            onMouseUp={dragScroll.onMouseUp}
            onMouseMove={dragScroll.onMouseMove}
          >
            <s.Table ref={tableRef}>
              <thead>
                <tr>
                  {columns.map((col, idx) => (
                    <s.Th
                      key={col.key as string}
                      align={idx === 0 ? (col.align ?? 'left') : 'center'}
                      width={col.width}
                    >
                      {col.label}
                    </s.Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        height: 120,
                        padding: 0,
                        border: 'none',
                        background: 'none',
                      }}
                    >
                      <NoResultContainer>
                        해당 기간/범위에 사례가 없습니다.
                      </NoResultContainer>
                    </td>
                  </tr>
                ) : (
                  <>
                    {visible.map((row, idx) => (
                      <tr key={idx}>
                        {columns.map((col, colIdx) => {
                          const value = col.render
                            ? col.render(row)
                            : row[col.key];

                          return (
                            <s.Td
                              key={col.key as string}
                              align={
                                colIdx === 0 ? (col.align ?? 'left') : 'center'
                              }
                            >
                              {value != null && value !== ''
                                ? col.unit
                                  ? col.unit === '원'
                                    ? `${formatCurrency(
                                        Number(String(value).replace(/,/g, '')),
                                      )}`
                                    : `${value}${col.unit}`
                                  : value
                                : ''}
                            </s.Td>
                          );
                        })}
                      </tr>
                    ))}

                    {avg && (
                      <s.AvgTr>
                        {columns.map((col, colIdx) => {
                          let value: string = ' ';

                          if (colIdx === 0) {
                            value = '평균';
                          } else if (
                            avgKeys.includes(col.key as keyof typeof avg)
                          ) {
                            const v = avg[col.key as string];

                            const num =
                              v != null
                                ? Number(String(v).replace(/,/g, ''))
                                : NaN;
                            if (!isNaN(num) && num !== 0) {
                              const trimmed = Math.floor(num * 100) / 100;
                              value =
                                col.unit === '원'
                                  ? `${formatCurrency(trimmed)}`
                                  : col.unit
                                    ? `${trimmed.toFixed(2)}${col.unit}`
                                    : trimmed.toFixed(2);
                            }
                          }

                          return (
                            <s.TdAvg
                              key={col.key as string}
                              align={
                                colIdx === 0 ? (col.align ?? 'left') : 'center'
                              }
                            >
                              {value}
                            </s.TdAvg>
                          );
                        })}
                      </s.AvgTr>
                    )}
                  </>
                )}
              </tbody>
            </s.Table>
          </TableWrap>
          {data.length > visibleCount && (
            <s.MoreButton onClick={() => setShowAll((v) => !v)}>
              <span>{showAll ? '접기' : '더보기'}</span>
              <img
                src="/assets/images/arrow/arrow-down-gray.svg"
                alt="더보기/접기"
                style={{
                  transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </s.MoreButton>
          )}
          {extraBottom}
        </>
      )}
    </Container>
  );
}

export default MyTable;

function useDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  let isDown = false;
  let startX = 0;
  let scrollLeft = 0;

  const onMouseDown = (e: React.MouseEvent) => {
    isDown = true;
    if (!ref.current) return;
    ref.current.classList.add('grabbing');
    startX = e.pageX - ref.current.offsetLeft;
    scrollLeft = ref.current.scrollLeft;
  };

  const onMouseLeave = () => {
    isDown = false;
    if (!ref.current) return;
    ref.current.classList.remove('grabbing');
  };

  const onMouseUp = () => {
    isDown = false;
    if (!ref.current) return;
    ref.current.classList.remove('grabbing');
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDown || !ref.current) return;
    e.preventDefault();
    const x = e.pageX - ref.current.offsetLeft;
    const walk = (x - startX) * 1.2;
    ref.current.scrollLeft = scrollLeft - walk;
  };

  return { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove };
}

const Container = styled.div`
  width: 100%;
  background-color: #fff;
  position: relative;
  angle: 0 deg;
  opacity: 1;
  padding-top: 24px;
  padding-right: 16px;
  padding-bottom: 40px;
  padding-left: 16px;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Description = styled.div`
  color: #787d87;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -2%;
  margin-bottom: 12px;

  p {
    padding: 0;
    margin: 0;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  margin-top: 16px;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  table {
    width: 100%;
    min-width: 100%;
    border-collapse: collapse;
  }
`;

const NoResultContainer = styled.div`
  position: absolute;
  top: 70%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #787d87;
  font-size: 15px;
  text-align: center;
`;
